import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Phase 9.7.15 — A&R Review History (Phase 1)
 *
 * Append-only history of every reviewer interaction with a track:
 *   - notes
 *   - status changes
 *   - score updates
 *   - feature / spotlight transitions (captured via status)
 *
 * Visible to track owner + tagged collaborators + admin/A&R staff.
 * Read-only modal — artists revisit feedback to track their growth.
 */

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (e) {
    return iso;
  }
};

const ROLE_BADGE = {
  admin: { label: "Admin", className: "bg-amber-500/20 text-amber-300 border-amber-500/40" },
  founder: { label: "Founder", className: "bg-amber-500/20 text-amber-300 border-amber-500/40" },
  moderator: { label: "Mod", className: "bg-sky-500/20 text-sky-300 border-sky-500/40" },
  ar: { label: "A&R", className: "bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/40" },
};

const STATUS_LABEL = {
  submitted: "Submitted",
  under_review: "Under Review",
  shortlisted: "Shortlisted",
  contacted: "Contacted",
  featured: "Featured",
  spotlight: "Spotlight",
  approved: "Approved",
  rejected: "Rejected",
};

const statusLabel = (s) => (s ? STATUS_LABEL[s] || s.replace("_", " ") : "");

const ReviewEntry = ({ entry }) => {
  const roleBadge = ROLE_BADGE[entry.reviewer_role] || ROLE_BADGE.admin;
  const hasNotes = entry.notes && entry.notes.trim().length > 0;
  const hasStatus = !!entry.new_status && entry.new_status !== entry.previous_status;
  const hasScore = entry.new_score !== null && entry.new_score !== undefined;

  return (
    <div
      className="p-4 rounded-lg bg-gradient-to-br from-purple-900/15 to-black border border-purple-500/20 hover:border-purple-500/40 transition-colors"
      data-testid={`review-entry-${entry.review_id}`}
    >
      <div className="flex items-start gap-3">
        {/* Reviewer avatar */}
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-fuchsia-600 to-amber-500 flex-shrink-0 flex items-center justify-center text-white font-bold">
          {entry.reviewer_photo ? (
            <img
              src={entry.reviewer_photo}
              alt={entry.reviewer_name}
              className="w-full h-full rounded-full object-cover"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ) : (
            (entry.reviewer_name || "R").charAt(0).toUpperCase()
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-white font-semibold text-sm">
              {entry.reviewer_name || "RGM Reviewer"}
            </span>
            <span
              className={`px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wide font-bold ${roleBadge.className}`}
            >
              {roleBadge.label}
            </span>
            <span className="text-gray-500 text-xs ml-auto">
              {formatDate(entry.created_at)}
            </span>
          </div>

          {/* Status transition */}
          {hasStatus && (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              <span className="text-gray-400">Status:</span>
              {entry.previous_status && (
                <>
                  <span className="px-2 py-0.5 rounded bg-gray-800/60 text-gray-400 uppercase">
                    {statusLabel(entry.previous_status)}
                  </span>
                  <span className="text-gray-500">→</span>
                </>
              )}
              <span className="px-2 py-0.5 rounded bg-purple-500/20 text-purple-300 uppercase font-semibold">
                {statusLabel(entry.new_status)}
              </span>
            </div>
          )}

          {/* Score */}
          {hasScore && (
            <div className="mt-2 flex items-center gap-2 text-xs">
              <span className="text-gray-400">Score:</span>
              <span
                className={`font-bold ${
                  entry.new_score >= 45
                    ? "text-green-400"
                    : entry.new_score >= 30
                    ? "text-yellow-400"
                    : "text-gray-300"
                }`}
              >
                {entry.new_score}/60
              </span>
              {entry.previous_score !== null && entry.previous_score !== undefined && (
                <span className="text-gray-500">(was {entry.previous_score})</span>
              )}
            </div>
          )}

          {/* Notes */}
          {hasNotes && (
            <div className="mt-3 p-3 rounded bg-black/50 border border-purple-500/20">
              <p className="text-[11px] uppercase tracking-wider text-fuchsia-300 mb-1 font-semibold">
                🎯 Reviewer Note
              </p>
              <p className="text-white text-sm whitespace-pre-line leading-relaxed">
                {entry.notes}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ReviewHistoryModal = ({ submissionId, trackTitle, open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !submissionId) return;
    const run = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/submissions/${submissionId}/reviews`);
        setData(res.data);
      } catch (err) {
        toast.error(
          err?.response?.data?.detail || "Failed to load review history"
        );
        setData({ entries: [], total: 0 });
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [open, submissionId]);

  const entries = data?.entries || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col bg-black border border-purple-500/40"
        data-testid="review-history-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2 text-lg">
            <span>📒</span>
            <span>Review History</span>
          </DialogTitle>
          <p className="text-sm text-gray-400 truncate">
            {trackTitle || data?.track_title || "Track"}
          </p>
        </DialogHeader>

        <div
          className="flex-1 overflow-y-auto space-y-3 pr-1 mt-2"
          data-testid="review-history-list"
        >
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="p-4 rounded-lg bg-gray-900/40 border border-gray-800 animate-pulse h-24"
                />
              ))}
            </div>
          ) : entries.length === 0 ? (
            <div
              className="p-8 text-center rounded-lg bg-gray-900/40 border border-gray-800"
              data-testid="review-history-empty"
            >
              <div className="text-4xl mb-3">🪶</div>
              <p className="text-white font-semibold mb-1">
                No reviews yet
              </p>
              <p className="text-sm text-gray-400">
                When an RGM A&R reviewer scores or leaves notes on this track,
                their feedback will appear here.
              </p>
            </div>
          ) : (
            entries.map((entry) => (
              <ReviewEntry key={entry.review_id} entry={entry} />
            ))
          )}
        </div>

        <div className="pt-3 mt-2 border-t border-purple-500/20 flex justify-end">
          <Button
            variant="outline"
            className="border-purple-500/40 text-purple-300"
            onClick={() => onOpenChange(false)}
            data-testid="review-history-close-btn"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewHistoryModal;
